import {
  CREATE_SESSION_PATH,
  playIntentLocationState,
  type PlayIntentLocationState,
} from '@/features/play-intents/lib/playIntentPrefill'
import { windowEndFor } from '@/features/play-intents/lib/playIntentWindows'

import type { NotificationResponse } from '../types/notification.types'

/**
 * Decide a dónde navegar al hacer click en una notificación.
 *
 * <p>Centralizado aquí para que el dropdown y la página `/notifications`
 * compartan la lógica — añadir un tipo nuevo solo requiere actualizar este
 * switch.</p>
 *
 * @returns la ruta destino, o {@code null} si la notif no es navegable
 *          (p.ej. SESSION_CANCELLED y `sessionId=null` por purga).
 */
export function notificationTarget(notification: NotificationResponse): string | null {
  // Tipos sin sesión con destino fijo (ignoran sessionId). Van antes del
  // early-return de sessionId=null para no degradar a /notifications.
  switch (notification.type) {
    case 'NO_SHOW_RECORDED':
    case 'NO_SHOW_WARNING':
    case 'NO_SHOW_COOLDOWN_ACTIVATED':
    case 'RELIABILITY_DECAY':
      return '/profile#reliability'
    // Cola de moderación del admin.
    case 'ADMIN_VENUE_PENDING':
    case 'ADMIN_VENUE_EDITED':
      return '/admin/locales'
    case 'ADMIN_CAMPAIGN_PENDING':
      return '/admin/hero-campaigns'
    // Cupo de emails al 80 %: la tarjeta «Emails» del dashboard admin.
    case 'ADMIN_EMAIL_QUOTA':
      return '/admin'
    // Comunicaciones en la campana: el destino del envío (el mismo que su push).
    case 'COMMUNICATION':
      return notification.communicationPath ?? '/sessions'
    // La insignia VIP vive en el perfil del usuario.
    case 'VIP_GRANTED':
      return '/profile#vip'
    // "Ya sois N con ganas…" → el chat del grupo, entrando por las ganas del destinatario
    // (spec 2026-10-12, R10). Sin ganas (aviso viejo), al asistente de crear partida
    // prerrellenado (el prefill viaja en `location.state`: ver `notificationTargetState`).
    case 'PLAY_INTENT_GROUP':
      return notification.playIntentId != null
        ? `/messages/group/${notification.playIntentId}`
        : CREATE_SESSION_PATH
  }

  if (notification.sessionId === null) {
    // La sesión asociada ya no existe (cron de purga > 1y): degradamos a la
    // bandeja completa para que el usuario tenga contexto.
    return '/notifications'
  }
  switch (notification.type) {
    case 'RATING_AVAILABLE':
      return `/sessions/${notification.sessionId}/rate`
    case 'SESSION_CANCELLED':
      // La sesión cancelada ya no es accesible vía detalle (404 deliberado).
      // Mandamos a /sessions/mine donde la verá en el tab Historial.
      return '/sessions/mine'
    case 'NEW_PARTICIPANT':
    case 'SESSION_REMINDER':
    case 'WAITLIST_PROMOTED':
    default:
      return `/sessions/${notification.sessionId}`
  }
}

/**
 * `location.state` que acompaña a {@link notificationTarget}. Solo lo lleva
 * PLAY_INTENT_GROUP sin ganas (va al asistente, no al chat): juego + ciudad + franja con
 * los que el asistente de crear partida se abre relleno. `undefined` en el resto (o si faltan datos: entonces
 * el asistente se abre vacío, como siempre).
 */
export function notificationTargetState(
  notification: NotificationResponse,
): PlayIntentLocationState | undefined {
  if (notification.type !== 'PLAY_INTENT_GROUP' || notification.playIntentId != null)
    return undefined
  const { playIntentGameId, playIntentCityCode, playIntentWindowStart, playIntentWindowKind } =
    notification
  if (playIntentGameId == null || !playIntentCityCode || !playIntentWindowStart) return undefined
  const windowEnd =
    notification.playIntentWindowEnd ??
    (playIntentWindowKind === 'WEEKDAYS' || playIntentWindowKind === 'WEEKEND'
      ? windowEndFor(playIntentWindowKind, playIntentWindowStart)
      : playIntentWindowStart)
  return playIntentLocationState({
    gameId: playIntentGameId,
    cityCode: playIntentCityCode,
    windowStart: playIntentWindowStart,
    windowEnd,
  })
}
